import * as React from 'react'
import styled, { css, FlattenSimpleInterpolation } from 'styled-components'
import * as S from '../../../styles'

export type CheckboxComponentProps = {
  error?: any
} & React.InputHTMLAttributes<HTMLInputElement>

/** will forward any other props to the underlying `<input>` */
function CheckboxComponent({
  className,
  error,
  ...inputProps
}: CheckboxComponentProps): React.ReactElement<CheckboxComponentProps> {
  return (
    <CheckboxWrapper className={className}>
      <S.input.HiddenFocusableInput type="checkbox" {...inputProps} />
      <Container>
        <ControlledCheckboxComponent error={error} disabled={inputProps.disabled} />
      </Container>
    </CheckboxWrapper>
  )
}

export const Checkbox = styled(CheckboxComponent)<CheckboxComponentProps>``
Checkbox.displayName = 'Checkbox'

const DefaultCheckmark = styled(S.checkbox.Checkmark).attrs({ $status: undefined })`
  display: inline-block;
`

const CheckedCheckmark = styled(S.checkbox.Checkmark).attrs({ $status: 'checked' })`
  display: none;
`

const FocusedCheckmark = styled(S.checkbox.Checkmark).attrs({ $status: 'focused' })`
  display: none;
`

const ErrorCheckmark = styled(S.checkbox.Checkmark).attrs({ $status: 'error' })`
  display: none;
`

export const styleCheckToggle = (
  input: any,
  follow: any,
  styles?: {
    focus?: FlattenSimpleInterpolation
    on?: FlattenSimpleInterpolation
    disabled?: FlattenSimpleInterpolation
  },
): FlattenSimpleInterpolation => css`
  ${input}:focus + ${follow} {
    ${FocusedCheckmark} {
      display: inline-block;
    }

    ${DefaultCheckmark}, ${CheckedCheckmark}, ${ErrorCheckmark} {
      display: none;
    }

    ${styles?.focus}
  }

  ${input}:checked + ${follow} {
    ${CheckedCheckmark} {
      display: inline-block;
    }

    ${DefaultCheckmark}, ${FocusedCheckmark}, ${ErrorCheckmark} {
      display: none;
    }

    ${styles?.on}
  }

  ${input}:disabled + ${follow} {
    ${styles?.disabled}
  }
`

export const ControlledCheckboxWrapper = styled.div<{ $isError: boolean }>`
  position: relative;
  width: 18px;
  height: 18px;
  ${({ $isError }) => {
    if ($isError) {
      return css`
        ${ErrorCheckmark} {
          display: inline-block;
        }
        ${DefaultCheckmark}, ${FocusedCheckmark}, ${CheckedCheckmark} {
          display: none;
        }
      `
    }
  }}
`

type ControlledCheckboxProps = {
  className?: string
  error?: any
  disabled?: boolean
}

function ControlledCheckboxComponent({
  className,
  error,
  disabled,
}: ControlledCheckboxProps): React.ReactElement<ControlledCheckboxProps> {
  return (
    <ControlledCheckboxWrapper className={className} $isError={Boolean(error)}>
      <DefaultCheckmark $disabled={disabled} />
      <FocusedCheckmark $disabled={disabled} />
      <CheckedCheckmark $disabled={disabled} />
      <ErrorCheckmark $disabled={disabled} />
    </ControlledCheckboxWrapper>
  )
}

export const ControlledCheckbox = styled(ControlledCheckboxComponent)``

const Container = styled.div``

const CheckboxWrapper = styled.div`
  ${styleCheckToggle(S.input.HiddenFocusableInput, Container, {})}
`
